/**
 * InsightPro™ — Company Selector
 * Shown after every login. Fetches all companies the user belongs to
 * from Firestore userMemberships/{uid}/companies and lets them pick one.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { auth } from '../utils/firebase';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';

const GOLD = '#D4A017';
const BG   = '#0a0a0a';
const CARD = '#141414';
const BORDER = '#2a2a2a';

export default function CompanySelector() {
  const navigate = useNavigate();
  const { setSelectedCompany, clearAllCompanyData } = useCompany();
  const [companies, setCompanies] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { navigate('/login', { replace: true }); return; }

    (async () => {
      try {
        const db = getFirestore();

        // Check whether the signed-in user is a superAdmin — they should see
        // every provisioned company, not just their userMemberships entries
        // (superAdmins typically have no memberships).
        const tokenResult = await user.getIdTokenResult();
        const isSuperAdmin = tokenResult.claims?.role === 'superAdmin'
                          || tokenResult.claims?.superAdmin === true;

        let list = [];
        if (isSuperAdmin) {
          // SuperAdmin: list all companies
          const snap = await getDocs(
            query(collection(db, 'companies'), orderBy('createdAt', 'desc'))
          );
          list = snap.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              companyId:   d.id,
              companyName: data.name || data.tradingName || '',
              name:        data.name || data.tradingName || '',
              logoUrl:     data.logoUrl || null,
              slug:        data.slug || '',
              role:        'superAdmin',
              branchScope: 'all',
            };
          });
        } else {
          // Regular user: list their userMemberships entries
          const snap = await getDocs(
            collection(db, 'userMemberships', user.uid, 'companies')
          );
          list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        }

        if (list.length === 0) {
          setError('No companies found for your account. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // If only one company, auto-select and go straight to dashboard
        if (list.length === 1) {
          setSelectedCompany(list[0]);
          window.location.replace('/');
          return;
        }

        // Clear any stale data from previous user
        clearAllCompanyData();

        setCompanies(list);
        setLoading(false);
      } catch (e) {
        setError('Could not load your companies. Please try again.');
        setLoading(false);
      }
    })();
  }, [navigate, setSelectedCompany, clearAllCompanyData]);

  const handleSelect = async (company) => {
    setSelectedCompany(company);
    // Record login for this company
    try {
      const token = await auth.currentUser?.getIdToken();
      const BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL;
      await axios.post(`${BASE}/recordLogin`, { companyId: company.id || company.companyId }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
    // Full reload ensures all pages re-fetch data for the new company
    window.location.replace('/');
  };

  // ── Layout ──────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', background: BG,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{
              width: 36, height: 36,
              background: `linear-gradient(135deg, #dba93a, ${GOLD})`,
              clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontFamily: 'sans-serif', fontWeight: 800, fontSize: 12, color: '#0a0a0a' }}>IP</span>
            </div>
            <span style={{ fontSize: 20, fontWeight: 700, color: '#eeeef8', letterSpacing: '-0.02em' }}>
              InsightPro<sup style={{ fontSize: 11, color: GOLD, verticalAlign: 'super' }}>™</sup>
            </span>
          </div>
          <div style={{ fontSize: 13, color: '#666', fontFamily: 'monospace', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Select Company
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', color: '#666', fontSize: 14 }}>
            <div style={{
              width: 32, height: 32, border: '2px solid #2a2a2a',
              borderTop: `2px solid ${GOLD}`, borderRadius: '50%',
              animation: 'spin 0.9s linear infinite',
              margin: '0 auto 16px',
            }} />
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            Loading your companies…
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(196,64,64,0.08)', border: '1px solid rgba(196,64,64,0.2)',
            borderRadius: 8, padding: '16px 20px', textAlign: 'center',
            color: '#e05252', fontSize: 14, lineHeight: 1.6,
          }}>{error}</div>
        )}

        {!loading && !error && (
          <>
            <p style={{ color: '#666', fontSize: 13, textAlign: 'center', marginBottom: 24 }}>
              Your account has access to {companies.length} companies. Select one to continue.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {companies.map(company => (
                <button
                  key={company.id}
                  onClick={() => handleSelect(company)}
                  style={{
                    background: CARD, border: `1px solid ${BORDER}`,
                    borderRadius: 12, padding: '16px 20px',
                    display: 'flex', alignItems: 'center', gap: 16,
                    cursor: 'pointer', textAlign: 'left', width: '100%',
                    transition: 'border-color 0.2s, transform 0.15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = GOLD;
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = BORDER;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Company logo or initials */}
                  <div style={{
                    width: 48, height: 48, borderRadius: 8, flexShrink: 0,
                    background: company.logoUrl ? '#fff' : `linear-gradient(135deg, #dba93a, ${GOLD})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', border: `1px solid ${BORDER}`,
                  }}>
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl} alt={company.companyName}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
                      />
                    ) : (
                      <span style={{ fontWeight: 800, fontSize: 16, color: '#0a0a0a' }}>
                        {(company.companyName || company.name || '?').slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Company info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#eeeef8', marginBottom: 3 }}>
                      {company.companyName || company.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#666', fontFamily: 'monospace', letterSpacing: '0.06em' }}>
                      {company.role || 'User'}{company.branchScope === 'all' ? ' · All branches' : ''}
                    </div>
                  </div>

                  <div style={{ color: '#444', fontSize: 18, flexShrink: 0 }}>›</div>
                </button>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <button
                onClick={async () => {
                  const { logout } = await import('../utils/firebase');
                  await logout();
                  navigate('/login', { replace: true });
                }}
                style={{
                  background: 'none', border: 'none', color: '#444',
                  fontSize: 12, fontFamily: 'monospace', letterSpacing: '0.1em',
                  textTransform: 'uppercase', cursor: 'pointer',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.target.style.color = '#666'}
                onMouseLeave={e => e.target.style.color = '#444'}
              >
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
